import { MAX_TAG_LENGTH, MAX_TAGS } from "@/tags/values"

import { requiresMetricQuestion } from "@/types/metrics/question-required-managers"

import type { ParseActionArgsResult, SofiaProposedAction } from "../model/types"
import { isRecord, readString } from "./actionArgumentReaders"

export const INVALID_METRIC_PROPOSAL_MESSAGE =
  "Ask Sofia for a fresh metric proposal to continue."

function metricCreatePromptProblem(
  args: Record<string, unknown>,
): string | undefined {
  return readString(args, "metric_manager") && args.prompt != null
    ? "catalog metrics require the rubric in config; remove the ignored top-level prompt"
    : undefined
}

export function parseMetricCreateArgs(
  action: SofiaProposedAction,
): ParseActionArgsResult {
  const { args } = action
  const problems: string[] = []
  const metric_name = readString(args, "metric_name")
  const description = readString(args, "description")
  const metric_manager = readString(args, "metric_manager")
  const config = isRecord(args.config) ? args.config : undefined
  const prompt = typeof args.prompt === "string" ? args.prompt : undefined
  const promptProblem = metricCreatePromptProblem(args)
  if (promptProblem) {
    problems.push(promptProblem)
  }
  if (!metric_name) {
    problems.push("missing metric_name")
  }
  if (!description) {
    problems.push("missing description")
  }
  if (args.metric_manager != null && !metric_manager) {
    problems.push("invalid metric_manager")
  }
  if (!metric_manager && !prompt?.trim()) {
    problems.push("missing prompt")
  }
  if (metric_manager && "config" in args && !config) {
    problems.push("invalid config")
  }
  if (
    !metric_manager &&
    args.config != null &&
    (!config || Object.keys(config).length > 0)
  ) {
    problems.push(
      "config requires metric_manager; use a catalog metric for these settings",
    )
  }
  if (
    metric_manager &&
    requiresMetricQuestion(metric_manager) &&
    (typeof config?.question !== "string" || !config.question.trim())
  ) {
    problems.push("missing catalog judge question")
  }
  if (prompt && Array.from(prompt).length > 50_000) {
    problems.push("prompt exceeds 50000 characters")
  }
  if (
    typeof config?.question === "string" &&
    Array.from(config.question).length > 50_000
  ) {
    problems.push("catalog judge question exceeds 50000 characters")
  }
  const { judge_mode, runtime_config, tags } = args
  if (
    "judge_mode" in args &&
    judge_mode !== "STANDARD" &&
    judge_mode !== "AGENTIC"
  ) {
    problems.push("invalid judge_mode")
  }
  if (
    "runtime_config" in args &&
    (!isRecord(runtime_config) ||
      Object.keys(runtime_config).length === 0 ||
      Object.keys(runtime_config).some(
        (key) => !["model_version", "thinking_enabled"].includes(key),
      ) ||
      (runtime_config.model_version != null &&
        (typeof runtime_config.model_version !== "string" ||
          !runtime_config.model_version.trim())) ||
      (runtime_config.thinking_enabled != null &&
        typeof runtime_config.thinking_enabled !== "boolean"))
  ) {
    problems.push("invalid runtime_config")
  }
  if (
    "tags" in args &&
    (!Array.isArray(tags) ||
      tags.length > MAX_TAGS ||
      tags.some(
        (tag) =>
          typeof tag !== "string" ||
          !tag.trim() ||
          Array.from(tag).length > MAX_TAG_LENGTH ||
          Array.from(tag.trim().toLowerCase()).length > MAX_TAG_LENGTH,
      ))
  ) {
    problems.push("invalid tags")
  }
  if (problems.length || !metric_name || !description) {
    return { ok: false, problems }
  }
  return {
    ok: true,
    action: {
      kind: "create_metric",
      args: {
        metric_name,
        description,
        ...(metric_manager
          ? { metric_manager, config: config ?? {} }
          : { prompt }),
        metric_type: readString(args, "metric_type"),
        ...(judge_mode === "STANDARD" || judge_mode === "AGENTIC"
          ? { judge_mode }
          : {}),
        ...(isRecord(runtime_config) ? { runtime_config } : {}),
        ...(Array.isArray(tags) ? { tags: tags as string[] } : {}),
      },
    },
  }
}
